import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { HubFormItem } from "@/components/inputs/HubFormItem";
import { RiskBandsAdvancedSettings } from "@/components/layouts/RiskBandsAdvancedSettings";
import { getArchetypeByValue, getArchetypes } from "@/features/branding";
import { defaultBuildYear, defaultInputValues } from "@/features/risk_bands";
import { keys } from "@/tools/aggregate/portfolio-export/schema";
import { PointWithMetadata } from "@/tools/create/lss-from-map/types";
import { RiskBandsAdvancedSettingsValues, RiskBandsArchetype } from "@/tools/risk-bands/types";
import { handoffToLargeSiteScreening } from "@/utils/handoff";
import { Button, Col, Form, InputNumber, Row, Select, Typography } from "antd";
import { Polygon } from "leaflet";
import { useEffect, useState } from "react";
const {
  Title
} = Typography;
interface SettingsStageSidebarProps {
  onGoBack: () => void;
  points: PointWithMetadata[];
  polygons: Polygon[];
}
const valueDividerOptions = [{
  label: "For whole site",
  value: "whole site"
}, {
  label: "Per individual grid point",
  value: "per grid point"
}];
const MAX_WHOLE_COST_VALUE = 100000000000; // 100 billion
const MAX_PER_POINT_COST_VALUE = 40000000; // 40 million

export const SettingsStageSidebar = ({
  onGoBack,
  points,
  polygons
}: SettingsStageSidebarProps) => {
  const archetypes = getArchetypes();
  const defaultArchetype = archetypes[0];
  const [advancedSettings, setAdvancedSettings] = useState<RiskBandsAdvancedSettingsValues>({
    archetype: defaultArchetype.value as RiskBandsArchetype,
    buildYear: {
      source: "archetype",
      amount: defaultArchetype.defaultBuildYear || defaultBuildYear
    },
    heightAboveGround: {
      source: "archetype",
      amount: defaultArchetype.defaultHeightAboveGround || 0
    }
  });
  const [valueDivider, setValueDivider] = useState(valueDividerOptions[0].value);
  const defaultMarketValue = defaultInputValues.aud.residential.marketValue;
  const defaultReplacementCost = defaultInputValues.aud.residential.replacementCost;
  const [marketValue, setMarketValue] = useState(defaultMarketValue);
  const [replacementCost, setReplacementCost] = useState(defaultReplacementCost);
  useEffect(() => {
    const archetype = archetypes.find(({
      value
    }) => value === advancedSettings.archetype);
    if (!archetype) {
      return;
    }
    setMarketValue(archetype.defaultInputValues.marketValue);
    setReplacementCost(archetype.defaultInputValues.replacementCost);
  }, [advancedSettings.archetype]);
  const startRun = () => {
    const handoffData = {
      assets: points.map(({
        point
      }) => ({
        [keys.id]: point.properties?.name,
        [keys.latitude]: point.geometry.coordinates[1],
        [keys.longitude]: point.geometry.coordinates[0],
        [keys.archetype]: (advancedSettings.archetype ? getArchetypeByValue(advancedSettings.archetype)?.name : undefined) as any,
        [keys.buildYear]: advancedSettings.buildYear.amount,
        [keys.floorHeight]: advancedSettings.heightAboveGround.amount,
        "Market Value (optional)": marketValue / points.length,
        "Replacement Cost (optional)": replacementCost / points.length
        // "Mortgage Rate (decimal, optional)": ,
      })),
      polygons
    };
    handoffToLargeSiteScreening(handoffData);
  };
  const commaFormatter = (value?: number) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const [estMarketValueError, setEstMarketValueError] = useState<string | undefined>(undefined);
  const [estReplacementCostError, setEstReplacementCostError] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (valueDivider === "whole site") setEstMarketValueError(marketValue > MAX_WHOLE_COST_VALUE ? `Est. Market Value cannot exceed ${commaFormatter(MAX_WHOLE_COST_VALUE)}` : undefined);else setEstMarketValueError(toPerPoint(marketValue) > MAX_PER_POINT_COST_VALUE ? `Est. Market Value cannot exceed ${commaFormatter(MAX_PER_POINT_COST_VALUE)}` : undefined);
  }, [valueDivider, marketValue]);
  useEffect(() => {
    if (valueDivider === "whole site") setEstReplacementCostError(replacementCost > MAX_WHOLE_COST_VALUE ? `Est. Replacement Cost cannot exceed ${commaFormatter(MAX_WHOLE_COST_VALUE)}` : undefined);else setEstReplacementCostError(toPerPoint(replacementCost) > MAX_PER_POINT_COST_VALUE ? `Est. Replacement cost cannot exceed ${commaFormatter(MAX_PER_POINT_COST_VALUE)}` : undefined);
  }, [valueDivider, replacementCost]);
  const toPerPoint = (value: number) => +(value / 12).toFixed(2);
  const toWholeSite = (value: number) => value * 12;
  return <Row gutter={[16, 16]} data-sentry-element="Row" data-sentry-component="SettingsStageSidebar" data-sentry-source-file="SettingsStageSidebar.tsx">
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Title level={4} data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">Asset Settings</Title>
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Title className="!mb-0" level={5} data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
          Design Settings
        </Title>
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <RiskBandsAdvancedSettings notice="NOTE: These settings are applied to all grid points within the Large Site polygon." values={advancedSettings} setValues={setAdvancedSettings} data-sentry-element="RiskBandsAdvancedSettings" data-sentry-source-file="SettingsStageSidebar.tsx" />
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Title className="!mb-0" level={5} data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
          <ContextualInfoButtonWithText id="financial" content={{
          title: "Financial Settings",
          body: "Please note that XDI's systems are currency-agnostic."
        }} data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="SettingsStageSidebar.tsx">
            Financial Settings
          </ContextualInfoButtonWithText>
        </Title>
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Select className="w-full" onSelect={setValueDivider} options={valueDividerOptions} value={valueDivider} data-sentry-element="Select" data-sentry-source-file="SettingsStageSidebar.tsx" />
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Form layout="vertical" data-sentry-element="Form" data-sentry-source-file="SettingsStageSidebar.tsx">
          <Row gutter={[16, 0]} data-sentry-element="Row" data-sentry-source-file="SettingsStageSidebar.tsx">
            <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
              <HubFormItem label={<>
                    <ContextualInfoButtonWithText id="marketValue">Est. Market Value</ContextualInfoButtonWithText>
                  </>} hasFeedback error={estMarketValueError ? {
              message: estMarketValueError
            } : undefined} data-sentry-element="HubFormItem" data-sentry-source-file="SettingsStageSidebar.tsx">
                <InputNumber addonAfter={valueDivider} className="w-full" formatter={commaFormatter} onChange={value => {
                const marketValue = value ?? defaultMarketValue;
                setMarketValue(valueDivider === "whole site" ? marketValue : toWholeSite(marketValue));
              }} value={valueDivider === "whole site" ? marketValue : toPerPoint(marketValue)} data-sentry-element="InputNumber" data-sentry-source-file="SettingsStageSidebar.tsx" />
              </HubFormItem>
            </Col>

            <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
              <HubFormItem label={<>
                    <ContextualInfoButtonWithText id="replacementCost">
                      Est. Replacement Cost
                    </ContextualInfoButtonWithText>
                  </>} hasFeedback error={estReplacementCostError ? {
              message: estReplacementCostError
            } : undefined} data-sentry-element="HubFormItem" data-sentry-source-file="SettingsStageSidebar.tsx">
                <InputNumber addonAfter={valueDivider} className="w-full" formatter={commaFormatter} onChange={value => {
                const replacementCost = value ?? defaultReplacementCost;
                setReplacementCost(valueDivider === "whole site" ? replacementCost : toWholeSite(replacementCost));
              }} value={valueDivider === "whole site" ? replacementCost : toPerPoint(replacementCost)} data-sentry-element="InputNumber" data-sentry-source-file="SettingsStageSidebar.tsx" />
              </HubFormItem>
            </Col>
          </Row>
        </Form>
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Button block onClick={onGoBack} data-sentry-element="Button" data-sentry-source-file="SettingsStageSidebar.tsx">
          Go Back
        </Button>
      </Col>
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
        <Button block type="primary" onClick={startRun} data-sentry-element="Button" data-sentry-source-file="SettingsStageSidebar.tsx">
          Proceed to Review
        </Button>
      </Col>
    </Row>;
};